import {
  EventLocations,
  EventMapLocations,
} from "../../interfaces/eventLocations";
import { EventSearchResponse } from "../../interfaces/eventSearchResponse";
import { EventLocationsContext, EventlocationsReducer } from "./";
import { FC, PropsWithChildren, useReducer } from "react";

export interface EventLocationsState {
  eventFindNearestSearch: boolean;
  eventLocations: EventLocations[];
  eventMapLocations: EventMapLocations[];
  eventTotalResults: number;
}

const Event_Locations_INITIAL_STATE: EventLocationsState = {
  eventFindNearestSearch: false,
  eventLocations: [],
  eventMapLocations: [],
  eventTotalResults: 0,
};

export const EventLocationsProvider: FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(
    EventlocationsReducer,
    Event_Locations_INITIAL_STATE
  );

  const clearEventLocations = () => {
    dispatch({
      type: "[Locations] - ClearEventLocations",
      payload: Event_Locations_INITIAL_STATE,
    });
  };

  const setEventLocations = (eventSearchResults: EventSearchResponse) => {
    const eventLocations: EventLocations[] = [];

    eventSearchResults.ResultList.forEach((event) => {
      const eventId = event.EventId || 0; // Assuming eventId is a number
      event.EventLocations.forEach((eventLocationDTO) => {
        const locationId = eventLocationDTO.Id || 0;

        const eventLocation: EventLocations = {
          distance: event.Distance,
          endDate: event.EndDate,
          endTime: event.EndTime,
          eventDescription: event.EventDescription,
          eventId: event.EventId,
          eventImageURL: event.EventImageURL,
          eventTitle: event.EventTitle,
          startDate: event.StartDate,
          startTime: event.StartTime,
          registration: event.Registration,
          address1: eventLocationDTO.Address1 || null,
          city: eventLocationDTO.City || null,
          countryCode: eventLocationDTO.CountryCode || null,
          countryId: eventLocationDTO.CountryId || null,
          id: eventId + locationId || null,
          latitude: eventLocationDTO.Latitude || null,
          longitude: eventLocationDTO.Longitude || null,
          locationName: eventLocationDTO.Name || null,
          postalCode: eventLocationDTO.PostCode || null,
          stateName: eventLocationDTO.StateName || null,
          stateAbbr: eventLocationDTO.StateAbbr || null,
          registrationURL: event.RegistrationURL,
        };

        eventLocations.push(eventLocation);
      });
    });

    const payload: EventLocationsState = {
      eventFindNearestSearch: eventSearchResults.FindNearestSearch,
      eventLocations: eventLocations,
      eventMapLocations: [],
      eventTotalResults: eventSearchResults.TotalResults,
    };

    dispatch({ type: "[Locations] - SetEventLocations", payload: payload });
  };

  const setMapEventLocations = (eventSearchResults: EventSearchResponse) => {
    const eventLocations: EventMapLocations[] = [];

    eventSearchResults.ResultList.forEach((event) => {
      const eventId = event.EventId || 0; // Assuming eventId is a number

      event.EventLocations.forEach((eventLocationDTO) => {
        const locationId = eventLocationDTO.Id || 0; // Assuming locationId is a number

        const eventLocation: EventMapLocations = {
          distance: event.Distance,
          endDate: event.EndDate,
          endTime: event.EndTime,
          eventDescription: event.EventDescription,
          eventId: event.EventId,
          eventImageURL: event.EventImageURL,
          eventTitle: event.EventTitle,
          startDate: event.StartDate,
          startTime: event.StartTime,
          registration: event.Registration,
          address1: eventLocationDTO.Address1 || null,
          city: eventLocationDTO.City || null,
          countryCode: eventLocationDTO.CountryCode || null,
          countryId: eventLocationDTO.CountryId || null,
          id: eventId + locationId || null,
          latitude: eventLocationDTO.Latitude || null,
          longitude: eventLocationDTO.Longitude || null,
          LocationName: eventLocationDTO.Name || null,
          postalCode: eventLocationDTO.PostCode || null,
          stateName: eventLocationDTO.StateName || null,
          stateAbbr: eventLocationDTO.StateAbbr || null,
          registrationURL: event.RegistrationURL,
        };

        eventLocations.push(eventLocation);
      });
    });

    dispatch({
      type: "[Locations] - SetMapEventLocations",
      payload: eventLocations as EventMapLocations[],
    });
  };

  return (
    <EventLocationsContext.Provider
      value={{
        ...state,
        clearEventLocations,
        setEventLocations,
        setMapEventLocations,
      }}
    >
      {children}
    </EventLocationsContext.Provider>
  );
};
